/**
 * Injectable clock.
 *
 * Two clocks are needed and they are not interchangeable:
 *
 * - **monotonic** — for durations. Immune to NTP steps, DST, and the user changing the
 *   system clock. Every duration, debounce and dwell-time comparison uses it.
 * - **wall clock (UTC)** — for timestamps that leave the device. Only ever for labelling.
 *
 * Injecting the clock is what makes the attention engine, event detector and calibration
 * maths deterministically testable: `ManualClock` advances by exact amounts, so a test for
 * "a 31-second episode is flagged potentially significant" is exact rather than flaky.
 */

export interface Clock {
  /** Seconds from an arbitrary origin; never decreases. */
  monotonic(): number;
  /** Current UTC time. */
  nowUtc(): Date;
  /** Resolve after `seconds`; a no-op for values <= 0. */
  sleep(seconds: number): Promise<void>;
}

/** Real clock used in production. */
export class SystemClock implements Clock {
  monotonic(): number {
    // performance.now() is monotonic and available in Node >= 16 and every browser.
    return performance.now() / 1000;
  }

  nowUtc(): Date {
    return new Date();
  }

  async sleep(seconds: number): Promise<void> {
    if (seconds <= 0) return;
    await new Promise<void>((resolve) => {
      setTimeout(resolve, seconds * 1000);
    });
  }
}

/**
 * Test clock. Wall time advances in lockstep with monotonic time, and `sleep` advances the
 * clock instead of waiting, so a rate-limited loop can be exercised without spending real
 * time.
 */
export class ManualClock implements Clock {
  private monotonicSeconds: number;
  private wallMs: number;

  constructor(startMonotonic = 1_000, startUtc: Date = new Date('2026-01-01T09:00:00.000Z')) {
    this.monotonicSeconds = startMonotonic;
    this.wallMs = startUtc.getTime();
  }

  monotonic(): number {
    return this.monotonicSeconds;
  }

  nowUtc(): Date {
    return new Date(this.wallMs);
  }

  async sleep(seconds: number): Promise<void> {
    this.advance(Math.max(0, seconds));
  }

  /** Move both clocks forward and return the new monotonic time. */
  advance(seconds: number): number {
    if (seconds < 0) throw new RangeError('ManualClock cannot move backwards');
    this.monotonicSeconds += seconds;
    this.wallMs += seconds * 1000;
    return this.monotonicSeconds;
  }
}
